import { workspaceTextFor, type WorkspaceText } from './interface-copy';
import { useWorkspaceText } from './interface-copy';
// "Made from · Used by" (collaboration v1, PR 6 §2): one collapsed panel for
// every object (Result, Skill, meeting, agent) and a chat's "About this
// chat". The server has already dropped every link the reader cannot
// open; nothing here counts what is missing.
import { useContext, useEffect, useId, useState } from 'react';
import { Popover } from 'radix-ui';
import { InChatActionsMenu } from '@/mobile/chat-actions';
import { useWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import type { WorkspaceIconName } from './icons';
import { livingApi } from './living-api';
import type { ItemLinks, LinkEnd, LinkItemKind } from './living-api';

const ICONS: Record<LinkItemKind, WorkspaceIconName> = {
  result: 'results',
  skill: 'skills',
  meeting: 'meetings',
  agent: 'play',
  chat: 'chat',
  file: 'file',
  source: 'source',
  knowledge_page: 'knowledge',
};

const firstName = (name: string | null, uiText: WorkspaceText = workspaceTextFor('en')) =>
  (name ?? '').split(' ')[0] || uiText('Someone');
const version = (end: LinkEnd, uiText: WorkspaceText = workspaceTextFor('en')) =>
  end.version ? ' ' + uiText('· version {value1}', { value1: end.version }) : '';

/** Authored labels, with object names and titles preserved as data. */
export function linkLabel(
  end: LinkEnd,
  userId: string | null | undefined,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  if (end.kind === 'chat')
    return end.ownerUserId && end.ownerUserId === userId
      ? uiText('Your chat “{title}”', { title: end.title })
      : uiText('{name}’s chat “{title}”', {
          name: firstName(end.ownerName, uiText),
          title: end.title,
        });
  if (end.kind === 'agent')
    return end.relation === 'refreshed'
      ? uiText('Kept up to date by {name}{version}', {
          name: end.title,
          version: version(end, uiText),
        })
      : `${end.title}${version(end, uiText)}`;
  if (end.kind === 'skill')
    return uiText('Skill: {name}{version}', { name: end.title, version: version(end, uiText) });
  if (end.kind === 'result') return uiText('Result “{title}”', { title: end.title });
  if (end.kind === 'meeting') return uiText('Meeting “{title}”', { title: end.title });
  return end.title;
}

const usageLabel = (answers: number, uiText: WorkspaceText = workspaceTextFor('en')) =>
  uiText(answers === 1 ? 'Used in {count} answer this week' : 'Used in {count} answers this week', {
    count: answers,
  });

export function summaryLabel(
  links: ItemLinks | null,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  if (!links) return '';
  const used = links.usedBy.length + (links.usage?.answers ? 1 : 0);
  const sources = uiText(links.madeFrom.length === 1 ? '{count} source' : '{count} sources', {
    count: links.madeFrom.length,
  });
  const places = uiText(used === 1 ? 'used in {count} place' : 'used in {count} places', {
    count: used,
  });
  return `${sources} · ${places}`;
}

// A link opens its object where the workspace opens it. A kind this client no
// longer shows (a retired company fact) follows its href like any view link.
function useOpenEnd() {
  const ws = useWorkspace();
  return (end: LinkEnd) => {
    if (end.kind === 'chat') return ws.openThread(end.id);
    if (['result', 'skill', 'file', 'source', 'meeting', 'agent'].includes(end.kind))
      return ws.openCanvas({
        kind: end.kind as 'result' | 'skill' | 'file' | 'source' | 'meeting' | 'agent',
        id: end.id,
      });
    if (end.kind === 'knowledge_page') return ws.openCanvas({ kind: 'page', id: end.id });
    window.location.hash = end.href;
  };
}

function LinkColumn({
  title,
  ends,
  usage,
}: {
  title: string;
  ends: LinkEnd[];
  usage?: ItemLinks['usage'];
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const open = useOpenEnd();
  const answers = usage?.answers ?? 0;
  return (
    <div className="ws-provenance-column">
      <h3>{title}</h3>
      {answers > 0 && (
        <span className="ws-provenance-usage">
          <WorkspaceIcon name="chat" size={14} />
          {usageLabel(answers, uiText)}
        </span>
      )}
      {ends.map((end) => (
        <button
          type="button"
          className="ws-provenance-link"
          key={`${end.kind}:${end.id}`}
          onClick={() => open(end)}
        >
          <WorkspaceIcon
            name={Object.hasOwn(ICONS, end.kind) ? ICONS[end.kind] : 'file'}
            size={14}
          />
          <span>{linkLabel(end, ws.user?.id, uiText)}</span>
        </button>
      ))}
      {!ends.length && !answers && <span className="ws-muted">{uiText('Nothing yet.')}</span>}
    </div>
  );
}

/** An object's links, read again when the workspace changes. */
function useItemLinks(kind: LinkItemKind, id: string) {
  const ws = useWorkspace();
  const [links, setLinks] = useState<ItemLinks | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    setFailed(false);
    livingApi
      .links(kind, id)
      .then((value) => {
        if (active) setLinks(value);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [kind, id, ws.data]);
  return { links, failed };
}

function ProvenanceSummary({ links, failed }: { links: ItemLinks | null; failed: boolean }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  return (
    <>
      <WorkspaceIcon name="history" size={14} />
      <b>{uiText('Made from · Used by')}</b>
      <span>{failed ? uiText('Could not load links') : summaryLabel(links, uiText)}</span>
    </>
  );
}

function ProvenanceBody({ links }: { links: ItemLinks }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  return (
    <div className="ws-provenance-body">
      <LinkColumn title={uiText('Made from')} ends={links.madeFrom} />
      <LinkColumn title={uiText('Used by')} ends={links.usedBy} usage={links.usage} />
    </div>
  );
}

// Read at once, for the summary line on the collapsed panel.
export function ProvenancePanel({ kind, id }: { kind: LinkItemKind; id: string }) {
  const { links, failed } = useItemLinks(kind, id);
  return (
    <details className="ws-provenance">
      <summary>
        <ProvenanceSummary links={links} failed={failed} />
        <WorkspaceIcon name="down" size={12} />
      </summary>
      {links && <ProvenanceBody links={links} />}
    </details>
  );
}

/** A chat's links laid flat: the popover (or the menu row) already is the disclosure. */
function ChatLinks({ id }: { id: string }) {
  const { links, failed } = useItemLinks('chat', id);
  return (
    <div
      className="ws-provenance ws-provenance-flat"
      data-state={failed ? 'failed' : links ? 'ready' : 'loading'}
    >
      <div className="ws-provenance-head">
        <ProvenanceSummary links={links} failed={failed} />
      </div>
      {links && <ProvenanceBody links={links} />}
    </div>
  );
}

/**
 * The chat header's "About this chat": where the chat came from (a fork's
 * origin) and what used it (Results made in it).
 * Only for a saved chat. A Radix popover (DESIGN-SPEC §6.9), so Esc and an
 * outside click close it; it renders in place to keep the header's palette.
 * Inside the narrow actions menu, which is already a popover, the links open
 * in place under the row.
 */
export function AboutChatButton() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const nested = useContext(InChatActionsMenu);
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const saved = Boolean(ws.threadId && ws.threads.some((thread) => thread.id === ws.threadId));
  useEffect(() => setOpen(false), [ws.threadId]);
  if (!saved) return null;
  if (nested)
    return (
      <div className="ws-about-chat">
        <button
          type="button"
          className="ws-text-button"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen((value) => !value)}
        >
          {uiRich('About this chat {value1}', { value1: <WorkspaceIcon name="down" size={12} /> })}
        </button>
        {open && (
          <div
            id={panelId}
            className="ws-about-chat-panel"
            role="region"
            aria-label={uiText('About this chat')}
          >
            <ChatLinks id={ws.threadId} />
          </div>
        )}
      </div>
    );
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div className="ws-about-chat">
        <Popover.Trigger asChild>
          <button type="button" className="ws-text-button">
            {uiText('About this chat')}
          </button>
        </Popover.Trigger>
        <Popover.Content
          align="end"
          sideOffset={6}
          collisionPadding={16}
          className="ws-about-chat-panel"
          aria-label={uiText('About this chat')}
        >
          <ChatLinks id={ws.threadId} />
        </Popover.Content>
      </div>
    </Popover.Root>
  );
}
